import React, { useState, useEffect } from 'react';
import { useAuth } from '../hooks/useAuth';
import PageHeader from '../components/PageHeader';
import {
  BarChart3,
  FileText,
  Download,
  Filter,
  TrendingUp,
  TrendingDown,
  Package,
  ShoppingCart,
  DollarSign,
  AlertTriangle,
  Users,
  Clock,
  Eye,
  Settings,
  Plus,
} from 'lucide-react';
import type { Reporte, FiltrosReportes } from '../types/reportes';

const Reportes: React.FC = () => {
  const { user } = useAuth();
  const [reportes, setReportes] = useState<Reporte[]>([]);
  const [filtros, setFiltros] = useState<FiltrosReportes>({});
  const [loading, setLoading] = useState(true);
  const [vistaActual, setVistaActual] = useState<'dashboard' | 'lista'>(
    'dashboard'
  );

  const puedeGenerarReportes = ['administrador', 'chef'].includes(
    user?.rol || ''
  );

  // Mock data loading
  useEffect(() => {
    setTimeout(() => {
      // Reportes mock
      setReportes([
        {
          id: '1',
          nombre: 'Inventario Mensual - Agosto 2024',
          descripcion: 'Reporte completo del estado del inventario',
          tipo: 'inventario',
          fechaGeneracion: new Date('2024-08-26'),
          periodo: {
            desde: new Date('2024-08-01'),
            hasta: new Date('2024-08-31'),
          },
          usuarioGenerador: {
            id: '1',
            nombre: 'Admin Usuario',
          },
          datos: {
            totalIngredientes: 45,
            valorTotalInventario: 2850000,
            ingredientesPorCategoria: [
              { categoria: 'Carnes', cantidad: 8, valor: 850000 },
              { categoria: 'Verduras', cantidad: 15, valor: 450000 },
              { categoria: 'Granos', cantidad: 12, valor: 680000 },
              { categoria: 'Lácteos', cantidad: 10, valor: 870000 },
            ],
            stockPorEstado: {
              normal: 32,
              bajo: 8,
              critico: 3,
              exceso: 2,
            },
            movimientosPorDia: [],
            ingredientesConMasMovimiento: [],
          },
        },
        {
          id: '2',
          nombre: 'Compras - Agosto 2024',
          descripcion: 'Análisis de órdenes de compra y proveedores',
          tipo: 'compras',
          fechaGeneracion: new Date('2024-08-25'),
          periodo: {
            desde: new Date('2024-08-01'),
            hasta: new Date('2024-08-31'),
          },
          usuarioGenerador: {
            id: '1',
            nombre: 'Admin Usuario',
          },
          datos: {
            totalOrdenes: 24,
            montoTotal: 1850000,
            montoPromedio: 77083,
            comprasPorProveedor: [
              {
                proveedor: 'Distribuidora Central',
                ordenes: 12,
                monto: 920000,
                porcentaje: 49.7,
              },
              {
                proveedor: 'Carnicería San Juan',
                ordenes: 8,
                monto: 680000,
                porcentaje: 36.8,
              },
              {
                proveedor: 'Verduras del Sur',
                ordenes: 4,
                monto: 250000,
                porcentaje: 13.5,
              },
            ],
            comprasPorMes: [],
            tiempoPromedioEntrega: 2.5,
            ingredientesMasComprados: [],
          },
        },
        {
          id: '3',
          nombre: 'Análisis de Costos - Q3 2024',
          descripcion: 'Evolución de costos del tercer trimestre',
          tipo: 'costos',
          fechaGeneracion: new Date('2024-08-20'),
          periodo: {
            desde: new Date('2024-07-01'),
            hasta: new Date('2024-09-30'),
          },
          usuarioGenerador: {
            id: '1',
            nombre: 'Admin Usuario',
          },
          datos: {
            costoTotalPeriodo: 5250000,
            costoPromedioDiario: 57065,
            costosPorCategoria: [
              { categoria: 'Carnes', monto: 2100000, porcentaje: 40 },
              { categoria: 'Lácteos', monto: 1312500, porcentaje: 25 },
              { categoria: 'Verduras', monto: 1050000, porcentaje: 20 },
              { categoria: 'Granos', monto: 787500, porcentaje: 15 },
            ],
            tendenciaCostos: [],
            ingredientesCaros: [],
            comparacionPeriodoAnterior: {
              variacion: 8.5,
              tipo: 'aumento',
            },
          },
        },
      ]);
      setLoading(false);
    }, 1000);
  }, []);

  const getReportIcon = (tipo: string) => {
    const iconos = {
      inventario: Package,
      compras: ShoppingCart,
      costos: DollarSign,
      consumo: BarChart3,
      vencimientos: AlertTriangle,
      proveedores: Users,
    };
    return iconos[tipo as keyof typeof iconos] || FileText;
  };

  const getReportColor = (tipo: string) => {
    const colores = {
      inventario:
        'bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-400',
      compras:
        'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-400',
      costos:
        'bg-purple-100 text-purple-800 dark:bg-purple-900/30 dark:text-purple-400',
      consumo:
        'bg-orange-100 text-orange-800 dark:bg-orange-900/30 dark:text-orange-400',
      vencimientos:
        'bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-400',
      proveedores:
        'bg-yellow-100 text-yellow-800 dark:bg-yellow-900/30 dark:text-yellow-400',
    };
    return (
      colores[tipo as keyof typeof colores] ||
      'bg-gray-100 text-gray-800 dark:bg-gray-900/30 dark:text-gray-400'
    );
  };

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('es-CL', {
      style: 'currency',
      currency: 'CLP',
    }).format(amount);
  };

  const formatFecha = (fecha: Date) => {
    return fecha.toLocaleDateString('es-CL', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
    });
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-900">
      <PageHeader
        title="Reportes y Analytics"
        subtitle="Análisis de datos y métricas del negocio"
        actions={
          puedeGenerarReportes ? (
            <div className="flex items-center space-x-3">
              <div className="flex items-center bg-white dark:bg-gray-800 rounded-lg border border-gray-300 dark:border-gray-600">
                <button
                  onClick={() => setVistaActual('dashboard')}
                  className={`px-3 py-2 text-sm font-medium rounded-l-lg transition-colors duration-200 ${
                    vistaActual === 'dashboard'
                      ? 'bg-blue-600 text-white'
                      : 'text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700'
                  }`}
                >
                  <BarChart3 className="h-4 w-4 mr-2 inline" />
                  Dashboard
                </button>
                <button
                  onClick={() => setVistaActual('lista')}
                  className={`px-3 py-2 text-sm font-medium rounded-r-lg transition-colors duration-200 ${
                    vistaActual === 'lista'
                      ? 'bg-blue-600 text-white'
                      : 'text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700'
                  }`}
                >
                  <FileText className="h-4 w-4 mr-2 inline" />
                  Lista
                </button>
              </div>
              <button className="inline-flex items-center px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg transition-colors duration-200">
                <Plus className="h-5 w-5 mr-2" />
                Generar Reporte
              </button>
            </div>
          ) : undefined
        }
      />

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {vistaActual === 'dashboard' ? (
          <>
            {/* Métricas principales */}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
              <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-200/50 dark:border-gray-700/50 p-6">
                <div className="flex items-center">
                  <div className="bg-blue-100 dark:bg-blue-900/30 p-3 rounded-lg">
                    <Package className="h-6 w-6 text-blue-600 dark:text-blue-400" />
                  </div>
                  <div className="ml-4">
                    <p className="text-sm font-medium text-gray-600 dark:text-gray-400">
                      Valor Inventario
                    </p>
                    <p className="text-2xl font-bold text-gray-900 dark:text-gray-100">
                      {formatCurrency(2850000)}
                    </p>
                    <div className="flex items-center mt-1">
                      <TrendingUp className="h-4 w-4 text-green-500 mr-1" />
                      <span className="text-sm text-green-600 dark:text-green-400">
                        +5.2%
                      </span>
                    </div>
                  </div>
                </div>
              </div>

              <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-200/50 dark:border-gray-700/50 p-6">
                <div className="flex items-center">
                  <div className="bg-green-100 dark:bg-green-900/30 p-3 rounded-lg">
                    <ShoppingCart className="h-6 w-6 text-green-600 dark:text-green-400" />
                  </div>
                  <div className="ml-4">
                    <p className="text-sm font-medium text-gray-600 dark:text-gray-400">
                      Compras del Mes
                    </p>
                    <p className="text-2xl font-bold text-gray-900 dark:text-gray-100">
                      {formatCurrency(1850000)}
                    </p>
                    <div className="flex items-center mt-1">
                      <TrendingDown className="h-4 w-4 text-red-500 mr-1" />
                      <span className="text-sm text-red-600 dark:text-red-400">
                        -2.1%
                      </span>
                    </div>
                  </div>
                </div>
              </div>

              <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-200/50 dark:border-gray-700/50 p-6">
                <div className="flex items-center">
                  <div className="bg-purple-100 dark:bg-purple-900/30 p-3 rounded-lg">
                    <DollarSign className="h-6 w-6 text-purple-600 dark:text-purple-400" />
                  </div>
                  <div className="ml-4">
                    <p className="text-sm font-medium text-gray-600 dark:text-gray-400">
                      Costo Promedio Diario
                    </p>
                    <p className="text-2xl font-bold text-gray-900 dark:text-gray-100">
                      {formatCurrency(57065)}
                    </p>
                    <div className="flex items-center mt-1">
                      <TrendingUp className="h-4 w-4 text-red-500 mr-1" />
                      <span className="text-sm text-red-600 dark:text-red-400">
                        +8.5%
                      </span>
                    </div>
                  </div>
                </div>
              </div>

              <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-200/50 dark:border-gray-700/50 p-6">
                <div className="flex items-center">
                  <div className="bg-red-100 dark:bg-red-900/30 p-3 rounded-lg">
                    <AlertTriangle className="h-6 w-6 text-red-600 dark:text-red-400" />
                  </div>
                  <div className="ml-4">
                    <p className="text-sm font-medium text-gray-600 dark:text-gray-400">
                      Items Críticos
                    </p>
                    <p className="text-2xl font-bold text-gray-900 dark:text-gray-100">
                      11
                    </p>
                    <div className="flex items-center mt-1">
                      <Clock className="h-4 w-4 text-orange-500 mr-1" />
                      <span className="text-sm text-orange-600 dark:text-orange-400">
                        3 por vencer
                      </span>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Gráficos principales */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-8">
              {/* Distribución por categorías */}
              <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-200/50 dark:border-gray-700/50 p-6">
                <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100 mb-4">
                  Valor por Categoría
                </h3>
                <div className="space-y-4">
                  {[
                    { categoria: 'Carnes', valor: 850000, porcentaje: 30 },
                    { categoria: 'Lácteos', valor: 870000, porcentaje: 31 },
                    { categoria: 'Granos', valor: 680000, porcentaje: 24 },
                    { categoria: 'Verduras', valor: 450000, porcentaje: 15 },
                  ].map((item) => (
                    <div key={item.categoria} className="flex items-center">
                      <div className="w-24 text-sm font-medium text-gray-900 dark:text-gray-100">
                        {item.categoria}
                      </div>
                      <div className="flex-1 mx-4">
                        <div className="w-full bg-gray-200 dark:bg-gray-700 rounded-full h-2">
                          <div
                            className="bg-blue-600 h-2 rounded-full transition-all duration-500"
                            style={{ width: `${item.porcentaje}%` }}
                          ></div>
                        </div>
                      </div>
                      <div className="w-32 text-right">
                        <div className="text-sm font-semibold text-gray-900 dark:text-gray-100">
                          {formatCurrency(item.valor)}
                        </div>
                        <div className="text-xs text-gray-500 dark:text-gray-400">
                          {item.porcentaje}%
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              {/* Top proveedores */}
              <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-200/50 dark:border-gray-700/50 p-6">
                <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100 mb-4">
                  Top Proveedores
                </h3>
                <div className="space-y-4">
                  {[
                    {
                      proveedor: 'Distribuidora Central',
                      monto: 920000,
                      ordenes: 12,
                    },
                    {
                      proveedor: 'Carnicería San Juan',
                      monto: 680000,
                      ordenes: 8,
                    },
                    {
                      proveedor: 'Verduras del Sur',
                      monto: 250000,
                      ordenes: 4,
                    },
                  ].map((item, index) => (
                    <div
                      key={item.proveedor}
                      className="flex items-center justify-between"
                    >
                      <div className="flex items-center">
                        <div
                          className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-bold text-white mr-3 ${
                            index === 0
                              ? 'bg-yellow-500'
                              : index === 1
                                ? 'bg-gray-400'
                                : 'bg-orange-500'
                          }`}
                        >
                          {index + 1}
                        </div>
                        <div>
                          <p className="font-medium text-gray-900 dark:text-gray-100">
                            {item.proveedor}
                          </p>
                          <p className="text-sm text-gray-500 dark:text-gray-400">
                            {item.ordenes} órdenes
                          </p>
                        </div>
                      </div>
                      <div className="text-right">
                        <p className="font-semibold text-gray-900 dark:text-gray-100">
                          {formatCurrency(item.monto)}
                        </p>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>

            {/* Reportes recientes */}
            <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-200/50 dark:border-gray-700/50 p-6">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100">
                  Reportes Recientes
                </h3>
                <button
                  onClick={() => setVistaActual('lista')}
                  className="text-blue-600 hover:text-blue-800 dark:text-blue-400 dark:hover:text-blue-300 text-sm font-medium"
                >
                  Ver todos
                </button>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                {reportes.slice(0, 3).map((reporte) => {
                  const IconoReporte = getReportIcon(reporte.tipo);
                  return (
                    <div
                      key={reporte.id}
                      className="border border-gray-200 dark:border-gray-700 rounded-lg p-4 hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors duration-200"
                    >
                      <div className="flex items-center mb-3">
                        <div className="bg-blue-100 dark:bg-blue-900/30 p-2 rounded-lg mr-3">
                          <IconoReporte className="h-5 w-5 text-blue-600 dark:text-blue-400" />
                        </div>
                        <span
                          className={`px-2 py-1 text-xs font-semibold rounded-full ${getReportColor(reporte.tipo)}`}
                        >
                          {reporte.tipo}
                        </span>
                      </div>
                      <h4 className="font-medium text-gray-900 dark:text-gray-100 mb-2">
                        {reporte.nombre}
                      </h4>
                      <p className="text-sm text-gray-600 dark:text-gray-400 mb-3">
                        {reporte.descripcion}
                      </p>
                      <div className="flex items-center justify-between text-xs text-gray-500 dark:text-gray-400">
                        <span>{formatFecha(reporte.fechaGeneracion)}</span>
                        <button className="text-blue-600 hover:text-blue-800 dark:text-blue-400 dark:hover:text-blue-300">
                          <Download className="h-4 w-4" />
                        </button>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          </>
        ) : (
          <>
            {/* Vista de lista de reportes */}
            <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-200/50 dark:border-gray-700/50 p-6 mb-6">
              <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between space-y-4 sm:space-y-0">
                <div className="flex-1 max-w-md">
                  <div className="relative">
                    <FileText className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-gray-400" />
                    <input
                      type="text"
                      placeholder="Buscar reportes..."
                      className="w-full pl-10 pr-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent dark:bg-gray-700 dark:text-gray-100"
                      value={filtros.busqueda || ''}
                      onChange={(e) =>
                        setFiltros({ ...filtros, busqueda: e.target.value })
                      }
                    />
                  </div>
                </div>

                <div className="flex items-center space-x-3">
                  <button className="inline-flex items-center px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors duration-200">
                    <Filter className="h-5 w-5 mr-2" />
                    Filtros
                  </button>
                  <button className="inline-flex items-center px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors duration-200">
                    <Settings className="h-5 w-5 mr-2" />
                    Plantillas
                  </button>
                </div>
              </div>
            </div>

            {/* Tabla de reportes */}
            <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-200/50 dark:border-gray-700/50 overflow-hidden">
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead className="bg-gray-50 dark:bg-gray-700">
                    <tr>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                        Reporte
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                        Tipo
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                        Período
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                        Generado
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                        Acciones
                      </th>
                    </tr>
                  </thead>
                  <tbody className="bg-white dark:bg-gray-800 divide-y divide-gray-200 dark:divide-gray-700">
                    {reportes.map((reporte) => {
                      const IconoReporte = getReportIcon(reporte.tipo);
                      return (
                        <tr
                          key={reporte.id}
                          className="hover:bg-gray-50 dark:hover:bg-gray-700"
                        >
                          <td className="px-6 py-4 whitespace-nowrap">
                            <div className="flex items-center">
                              <div className="bg-blue-100 dark:bg-blue-900/30 p-2 rounded-lg mr-3">
                                <IconoReporte className="h-5 w-5 text-blue-600 dark:text-blue-400" />
                              </div>
                              <div>
                                <div className="text-sm font-medium text-gray-900 dark:text-gray-100">
                                  {reporte.nombre}
                                </div>
                                <div className="text-sm text-gray-500 dark:text-gray-400">
                                  {reporte.descripcion}
                                </div>
                              </div>
                            </div>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap">
                            <span
                              className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${getReportColor(reporte.tipo)}`}
                            >
                              {reporte.tipo}
                            </span>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 dark:text-gray-100">
                            {formatFecha(reporte.periodo.desde)} -{' '}
                            {formatFecha(reporte.periodo.hasta)}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap">
                            <div className="text-sm text-gray-900 dark:text-gray-100">
                              {formatFecha(reporte.fechaGeneracion)}
                            </div>
                            <div className="text-sm text-gray-500 dark:text-gray-400">
                              {reporte.usuarioGenerador.nombre}
                            </div>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                            <div className="flex items-center space-x-2">
                              <button
                                title="Ver reporte"
                                className="text-blue-600 hover:text-blue-900 dark:text-blue-400 dark:hover:text-blue-300"
                              >
                                <Eye className="h-4 w-4" />
                              </button>
                              <button
                                title="Descargar"
                                className="text-green-600 hover:text-green-900 dark:text-green-400 dark:hover:text-green-300"
                              >
                                <Download className="h-4 w-4" />
                              </button>
                            </div>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default Reportes;
